import { CartData } from 'lib/core/data/quote/cart/types'
import { DEFAULT_CURRENCY_CODE } from 'lib/core/resources/constants'
import { getPriceValues } from 'lib/core/resources/helpers'
import { AddToCart, CategoryView, Checkout, ProductClick, ProductView, RemoveFromCart } from './types'
import { ProductData } from 'lib/types'

enum Trigger {
  Purchase = 'purchase',
  AddToCart = 'addToCart',
  RemoveFromCart = 'removeFromCart',
  ProductDetail = 'productDetail',
  ProductImpression = 'productImpression',
  ProductClick = 'productClick',
  Checkout = 'checkout'
}

export enum CheckoutDescription {
  Shipping = 'shipping',
  Payment = 'payment'
}

export enum CheckoutStep {
  Shipping = '1',
  Payment = '2'
}

export type ListPages = 'Search Page' | 'Category Page' | 'Upsell Products' | 'Other'

export type ProductProcess = (productData: ProductData) => Record<string, any>
let productProcess: ProductProcess = (productData) => {
  return productData
}

export const GTM = {
  setProductProcess: (handler: ProductProcess) => {
    productProcess = handler
  },

  /**
   * GTM purchase trigger
   * @docs: https://developers.google.com/tag-manager/enhanced-ecommerce#purchases
   */
  purchase: ({ orderId, cart }: { orderId: string, cart: CartData }) => {
    if (process.env.REACT_APP_DISABLE_GTM_GA3_EVENTS === 'true') {
      return
    }
    // @ts-expect-error
    if (!window.dataLayer) {
      return
    }
    const prices = cart.prices
    const products = cart.items.map(cartItem => {
      const variant = cartItem.variantProduct
      const { sku, name, price } = cartItem.product
      const extraData = productProcess(variant ?? cartItem.product)
      const [amount] = variant
        ? getPriceValues(variant.price)
        : price ? getPriceValues(price) : [0, DEFAULT_CURRENCY_CODE]

      return {
        id: variant ? variant.sku : (sku ?? ''),
        name: variant ? variant.name : (name ?? ''),
        price: amount.toString(),
        quantity: cartItem.quantity.toString(),
        ...extraData
      }
    })

    // @ts-expect-error
    window.dataLayer.push({
      event: Trigger.Purchase,
      ecommerce: {
        purchase: {
          actionField: {
            id: orderId,
            revenue: prices.grandTotal.value.toString(),
            tax: prices.appliedTaxes.reduce((acc: number, element) => {
              return acc + element.amount.value
            }, 0).toString(),
            shipping: cart.shippingAddress?.selectedShippingMethod?.amount.value.toString() ?? '0',
            coupon: cart.appliedCoupon ?? '',
            action: Trigger.Purchase
          },
          products,
          currencyCode: cart.currencyCode
        }
      }
    })
  },

  /**
   * GTM addToCart trigger
   * @docs: https://developers.google.com/tag-manager/enhanced-ecommerce#cart
   */
  addToCart: ({ currencyCode, products, pwaCart }: AddToCart) => {
    if (process.env.REACT_APP_DISABLE_GTM_GA3_EVENTS === 'true') {
      return
    }
    // @ts-expect-error
    if (!window.dataLayer) {
      return
    }

    // @ts-expect-error
    window.dataLayer.push({
      event: Trigger.AddToCart,
      ecommerce: {
        currencyCode,
        add: {
          products: products.map(product => {
            const realProduct = pwaCart?.items.find(prod => prod.product.sku === product.id)?.product

            return {
              ...product,
              ...(realProduct ? productProcess(realProduct) : {})
            }
          })
        }
      }
    })
  },

  /**
   * GTM removeFromCart trigger
   * @docs: https://developers.google.com/tag-manager/enhanced-ecommerce#cart
   */
  removeFromCart: ({ products }: RemoveFromCart) => {
    if (process.env.REACT_APP_DISABLE_GTM_GA3_EVENTS === 'true') {
      return
    }
    // @ts-expect-error
    if (!window.dataLayer) {
      return
    }

    // @ts-expect-error
    window.dataLayer.push({
      event: Trigger.RemoveFromCart,
      ecommerce: {
        remove: {
          products
        }
      }
    })
  },

  /**
   * GTM productImpressions trigger
   * @docs: https://developers.google.com/tag-manager/enhanced-ecommerce
   */
  productImpressions: ({ products }: CategoryView) => {
    if (process.env.REACT_APP_DISABLE_GTM_GA3_EVENTS === 'true') {
      return
    }
    // @ts-expect-error
    if (!window.dataLayer) {
      return
    }

    // @ts-expect-error
    window.dataLayer.push({
      event: Trigger.ProductImpression,
      ecommerce: {
        impressions: products
      }
    })
  },

  /**
   * GTM productClick trigger
   * @docs: https://developers.google.com/tag-manager/enhanced-ecommerce#product-clicks
   */
  productClick: ({ product, list }: ProductClick) => {
    // @ts-expect-error
    if (!window.dataLayer) {
      return
    }

    // @ts-expect-error
    window.dataLayer.push({
      event: Trigger.ProductClick,
      ecommerce: {
        actionField: { list },
        impressions: product
      }
    })
  },

  /**
   * GTM productDetail trigger
   * @docs: https://developers.google.com/tag-manager/enhanced-ecommerce#details
   */
  productDetail: ({ product }: ProductView) => {
    if (process.env.REACT_APP_DISABLE_GTM_GA3_EVENTS === 'true') {
      return
    }
    // @ts-expect-error
    if (!window.dataLayer) {
      return
    }

    const endProduct = {
      id: product.sku,
      price: product.price.minimalPrice.amount.value,
      name: product.name,
      ...productProcess(product)
    }

    // @ts-expect-error
    window.dataLayer.push({
      event: Trigger.ProductDetail,
      ecommerce: {
        detail: {
          products: [endProduct]
        }
      }
    })
  },

  /**
   * GTM checkout trigger
   * @docs: https://developers.google.com/tag-manager/enhanced-ecommerce#checkout
   * description: shipping | payment
   * step: 1 | 2
   * Add the trigger call in the application if is needed
   * in shipping and billing pages
   */
  checkout: ({ products, step, description }: Checkout) => {
    if (process.env.REACT_APP_DISABLE_GTM_GA3_EVENTS === 'true') {
      return
    }
    // @ts-expect-error
    if (!window.dataLayer) {
      return
    }

    // @ts-expect-error
    window.dataLayer.push({
      event: Trigger.Checkout,
      ecommerce: {
        checkout: {
          actionField: { step, description },
          products: products
        }
      }
    })
  }
}
